/* UGC Video Carousel
   - Infinite loop: the slides are cloned on both sides of the track. Moving onto a
     clone animates normally, then the track snaps (without animation) to the matching
     real slide, so it feels endless in both directions.
   - Drag / swipe (pointer events), click a slide to focus it, click an indicator,
     arrow keys when the track is focused.
   - Only the active slide's video plays; the others are paused. */

(function () {
  'use strict';

  var TRANSITION_MS = 420;
  var SWIPE_THRESHOLD = 40;
  var MAX_COPIES = 6;

  function mod(value, length) {
    return ((value % length) + length) % length;
  }

  function initCarousel(wrapper) {
    if (!wrapper || wrapper.getAttribute('data-vc-ready') === 'true') return;

    var track = wrapper.querySelector('.carousel-track');
    var container = wrapper.querySelector('.carousel-track-container');
    if (!track || !container) return;

    var originals = Array.prototype.slice.call(track.querySelectorAll('.carousel-item'));
    var count = originals.length;
    if (!count) return;

    wrapper.setAttribute('data-vc-ready', 'true');

    var dragCursor = wrapper.querySelector('.drag-cursor');
    var indicators = Array.prototype.slice.call(wrapper.querySelectorAll('.carousel-indicators .indicator'));
    var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var items = originals;
    var base = 0;
    var current = 0;
    var gap = 20;
    var inactiveWidth = 0;
    var currentTranslate = 0;
    var prevTranslate = 0;
    var isDragging = false;
    var didDrag = false;
    var startX = 0;
    var activePointerId = null;
    var settleTimer = null;
    var resizeTimer = null;

    function readGap() {
      var value = parseFloat(window.getComputedStyle(track).columnGap);
      if (!isNaN(value)) gap = value;
    }

    function measure(exclude) {
      readGap();
      var sample = null;
      for (var i = 0; i < items.length; i++) {
        if (!items[i].classList.contains('active') && items[i] !== exclude) {
          sample = items[i];
          break;
        }
      }
      if (!sample) sample = items[0];
      /* The sample is a settled inactive slide (never the one that just lost focus), so its width is stable */
      inactiveWidth = sample.getBoundingClientRect().width;
    }

    function makeClone(item) {
      var clone = item.cloneNode(true);
      clone.classList.remove('active');
      clone.setAttribute('data-clone', 'true');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-shopify-editor-block');
      var videos = clone.querySelectorAll('video');
      for (var i = 0; i < videos.length; i++) {
        videos[i].removeAttribute('autoplay');
        videos[i].setAttribute('preload', 'none');
      }
      var links = clone.querySelectorAll('a');
      for (var j = 0; j < links.length; j++) {
        links[j].setAttribute('tabindex', '-1');
      }
      return clone;
    }

    function buildLoop() {
      if (count < 2) return;
      measure();
      var setWidth = count * (inactiveWidth + gap);
      var copies = setWidth > 0 ? Math.ceil(container.getBoundingClientRect().width / setWidth) + 1 : 1;
      copies = Math.max(1, Math.min(MAX_COPIES, copies));

      var before = document.createDocumentFragment();
      var after = document.createDocumentFragment();
      for (var c = 0; c < copies; c++) {
        for (var i = 0; i < count; i++) {
          before.appendChild(makeClone(originals[i]));
          after.appendChild(makeClone(originals[i]));
        }
      }
      track.insertBefore(before, track.firstChild);
      track.appendChild(after);

      items = Array.prototype.slice.call(track.querySelectorAll('.carousel-item'));
      base = copies * count;
    }

    function positionFor(physicalIndex) {
      return -(physicalIndex * (inactiveWidth + gap));
    }

    function playVideo(item, fromItem) {
      var video = item.querySelector('video');
      if (!video) return;
      if (fromItem && fromItem !== item) {
        var fromVideo = fromItem.querySelector('video');
        if (fromVideo && !isNaN(fromVideo.currentTime)) {
          try { video.currentTime = fromVideo.currentTime; } catch (e) {}
        }
      }
      var promise = video.play();
      if (promise && typeof promise.catch === 'function') promise.catch(function () {});
    }

    function pauseVideo(item) {
      var video = item.querySelector('video');
      if (video && !video.paused) video.pause();
    }

    function updateIndicators() {
      var real = mod(current, count);
      for (var i = 0; i < indicators.length; i++) {
        indicators[i].classList.toggle('active', i === real);
        indicators[i].setAttribute('aria-current', i === real ? 'true' : 'false');
      }
    }

    function applyTranslate(value, animate) {
      currentTranslate = value;
      prevTranslate = value;
      if (!animate || reducedMotion) track.classList.add('no-transition');
      track.style.transform = 'translateX(' + value + 'px)';
      if (!animate || reducedMotion) {
        void track.offsetWidth;
        track.classList.remove('no-transition');
      }
    }

    function setActive(virtualIndex, animate, keepTime) {
      var previous = items[base + current];
      current = virtualIndex;
      var physical = base + current;
      if (physical < 0 || physical >= items.length) {
        current = mod(current, count);
        physical = base + current;
      }

      for (var i = 0; i < items.length; i++) {
        if (i === physical) {
          items[i].classList.add('active');
        } else {
          items[i].classList.remove('active');
          pauseVideo(items[i]);
        }
      }
      playVideo(items[physical], keepTime ? previous : null);

      measure(previous);
      applyTranslate(positionFor(physical), animate);
      updateIndicators();

      if (settleTimer) clearTimeout(settleTimer);
      if (current < 0 || current >= count) {
        settleTimer = setTimeout(settle, animate && !reducedMotion ? TRANSITION_MS : 0);
      }
    }

    function settle() {
      settleTimer = null;
      if (current >= 0 && current < count) return;
      setActive(mod(current, count), false, true);
    }

    function goTo(virtualIndex) {
      setActive(virtualIndex, true, false);
    }

    function step(delta) {
      goTo(current + delta);
    }

    /* Drag / swipe */
    function onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (settleTimer) {
        clearTimeout(settleTimer);
        settle();
      }
      isDragging = true;
      didDrag = false;
      startX = event.clientX;
      activePointerId = event.pointerId;
      prevTranslate = currentTranslate;
      track.classList.add('is-dragging');
      if (dragCursor) dragCursor.style.transform = 'translate(-50%, -50%) scale(0.9)';
      if (event.pointerType === 'mouse') event.preventDefault();
    }

    function onPointerMove(event) {
      if (!isDragging || event.pointerId !== activePointerId) return;
      var diff = event.clientX - startX;
      if (Math.abs(diff) > 5) didDrag = true;
      currentTranslate = prevTranslate + diff;
      track.style.transform = 'translateX(' + currentTranslate + 'px)';
    }

    function onPointerUp(event) {
      if (!isDragging || (event && event.pointerId !== activePointerId)) return;
      isDragging = false;
      activePointerId = null;
      track.classList.remove('is-dragging');
      if (dragCursor) dragCursor.style.transform = '';

      var moved = currentTranslate - prevTranslate;
      var slideWidth = inactiveWidth + gap;
      var change = slideWidth > 0 ? Math.round(-moved / slideWidth) : 0;
      if (change === 0) {
        if (moved < -SWIPE_THRESHOLD) change = 1;
        else if (moved > SWIPE_THRESHOLD) change = -1;
      }
      goTo(current + change);

      setTimeout(function () { didDrag = false; }, 100);
    }

    /* Click a slide to focus it; let the CTA link through unless we were dragging */
    function onTrackClick(event) {
      var cta = event.target.closest ? event.target.closest('.carousel-item__cta') : null;
      if (didDrag) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (cta) return;
      var item = event.target.closest ? event.target.closest('.carousel-item') : null;
      if (!item) return;
      var physical = items.indexOf(item);
      if (physical < 0) return;
      goTo(physical - base);
    }

    function onKeyDown(event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        step(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        step(-1);
      }
    }

    /* Custom cursor on desktop */
    if (dragCursor) {
      container.addEventListener('mouseenter', function () { dragCursor.classList.add('is-active'); });
      container.addEventListener('mouseleave', function () { dragCursor.classList.remove('is-active'); });
      container.addEventListener('mousemove', function (event) {
        dragCursor.style.left = event.clientX + 'px';
        dragCursor.style.top = event.clientY + 'px';
      });
    }

    track.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    track.addEventListener('click', onTrackClick, true);
    track.addEventListener('dragstart', function (event) { event.preventDefault(); });
    container.addEventListener('keydown', onKeyDown);

    indicators.forEach(function (dot, index) {
      dot.setAttribute('role', 'button');
      dot.setAttribute('tabindex', '0');
      dot.setAttribute('aria-label', 'Go to video ' + (index + 1));
      dot.addEventListener('click', function () { goTo(index); });
      dot.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          goTo(index);
        }
      });
    });

    function realign() {
      setActive(mod(current, count), false, true);
    }

    window.addEventListener('resize', function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(realign, 120);
    });
    if (document.readyState !== 'complete') {
      window.addEventListener('load', realign);
    }

    /* Theme editor: focus the block that was selected in the sidebar */
    wrapper.addEventListener('shopify:block:select', function (event) {
      var item = event.target && event.target.closest ? event.target.closest('.carousel-item') : null;
      if (!item) return;
      var index = parseInt(item.getAttribute('data-index'), 10);
      if (!isNaN(index)) goTo(index);
    });

    buildLoop();
    setActive(0, false, false);
  }

  /* The section stylesheet defines --vc-gap; until it has applied, measurements would be wrong */
  function whenStyled(wrapper, callback, attempts) {
    var ready = window.getComputedStyle(wrapper).getPropertyValue('--vc-gap').trim() !== '';
    if (ready || attempts <= 0) {
      callback();
      return;
    }
    setTimeout(function () { whenStyled(wrapper, callback, attempts - 1); }, 50);
  }

  function initAll(root) {
    var wrappers = (root || document).querySelectorAll('.video-carousel-wrapper');
    for (var i = 0; i < wrappers.length; i++) {
      (function (wrapper) {
        whenStyled(wrapper, function () { initCarousel(wrapper); }, 60);
      })(wrappers[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
